// Which machines are greyed out when a subscription payment has failed.
//
// £50 a month covers ten machines, then £5 each. Going over the allowance is
// fine — the subscription follows the fleet. It is a *failed payment* that
// greys the extras out, and only in the interface: writes are never blocked,
// so an operator can still submit a check against a greyed machine. We are
// never the reason a check wasn't recorded.
//
// The same rule as functions/billing-machines.js decides which machines count,
// so what greys out always matches what Stripe was asked to charge for.

const DEFAULT_INCLUDED = 10;

export function countsTowardsBilling(machine) {
  if (!machine) return false;
  if (machine.disposed === true) return false;
  if (machine.status === 'disposed' || machine.status === 'sold') return false;
  return true;
}

const addedAt = (m) => {
  const c = m && m.createdAt;
  if (!c) return 0; // no date means an early record — treat as oldest, never greyed first
  if (typeof c.toMillis === 'function') return c.toMillis();
  const t = new Date(c).getTime();
  return Number.isFinite(t) ? t : 0;
};

// True only when the org is both unpaid and has actually had a payment fail.
// Requiring the timestamp as well as the status keeps trialing and
// grandfathered organisations — which are not 'active' either — out of this.
export function isPaymentFailing(org) {
  const plan = (org && org.plan) || {};
  return plan.status !== 'active' && !!plan.lastPaymentFailedAt;
}

// The ids to grey out: everything beyond the included allowance, most recently
// added first. Returns an empty Set whenever nothing should grey.
export function overAllowanceMachineIds(machines, org) {
  if (!isPaymentFailing(org)) return new Set();

  const plan = (org && org.plan) || {};
  const included = Number.isFinite(Number(plan.includedMachines))
    ? Number(plan.includedMachines)
    : DEFAULT_INCLUDED;

  const billable = (machines || [])
    .filter(countsTowardsBilling)
    .sort((a, b) => addedAt(b) - addedAt(a)); // newest first

  return new Set(billable.slice(0, Math.max(0, billable.length - included)).map(m => m.id));
}
